import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, UserPlus, Copy, Trash, Check } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * FamilyPlanPanel — rendered on the Profile page. Shows two distinct
 * states back-to-back:
 *
 *   1. **Owner view**: full invite management for the Family Plan the
 *      caller purchased — remaining slots, invite form, per-row status
 *      (pending / joined / revoked) and revoke buttons for pending rows.
 *
 *   2. **Member view**: an informational card showing which owner's
 *      Family Plan the caller is part of + when it expires.
 *
 * If the caller is neither an owner nor a member, the panel renders a
 * subtle "Not on a Family Plan" row with a Pricing CTA.
 */
export default function FamilyPlanPanel() {
  const [plan, setPlan] = useState(null);
  const [invites, setInvites] = useState([]);
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [owner, member] = await Promise.all([
        api.get("/family-plan"),
        api.get("/family-plan/membership"),
      ]);
      setPlan(owner.data.plan);
      setInvites(owner.data.invites || []);
      setMembership(member.data.membership);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e?.preventDefault?.();
    const name = form.name.trim();
    const email = form.email.trim();
    if (!name || !email) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      await api.post("/family-plan/invites", { name, email });
      setForm({ name: "", email: "" });
      await load();
      toast.success(`Invitation sent to ${name}. They'll get an email with a code.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send invite");
    } finally { setBusy(false); }
  };

  const removeMember = async (inv) => {
    const who = inv.joined_name || inv.invitee_name || "this member";
    if (!window.confirm(`Remove ${who} from your Family Plan? They keep their own recipes and go back to the free Taster plan, and the seat is free for someone else.`)) return;
    setBusy(true);
    try {
      await api.delete(`/family-plan/members/${inv.used_by_user_id}`);
      await load();
      toast.success("Member removed — the seat is free again.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove member");
    } finally { setBusy(false); }
  };

  const revoke = async (code) => {
    if (!window.confirm("Revoke this pending invite? The code will stop working.")) return;
    setBusy(true);
    try {
      await api.delete(`/family-plan/invites/${code}`);
      await load();
      toast.success("Invite revoked — the slot is free again.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    } finally { setBusy(false); }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Code copied — paste it into a message if the email didn't arrive.");
    } catch { toast.error("Copy failed — select the code manually."); }
  };

  if (loading) return null;

  // ====== NON-OWNER + NON-MEMBER ======
  if (!plan && !membership) {
    return (
      <section className="bg-rf-cream-alt rounded-3xl p-6 sm:p-8 border border-rf-ink/5" data-testid="family-plan-empty">
        <div className="flex items-center gap-2 mb-2">
          <Users size={20} weight="duotone" className="text-rf-orange" />
          <h2 className="font-display text-xl font-black text-rf-ink">Family Plan</h2>
        </div>
        <p className="text-sm text-rf-ink-soft">
          One subscription that covers you plus 4 friends or family members for £100 a year. {" "}
          <a href="/pricing" className="text-rf-orange font-bold underline">Learn more on the pricing page</a>.
        </p>
      </section>
    );
  }

  // ====== MEMBER (not owner) ======
  if (!plan && membership) {
    return (
      <section className="bg-rf-cream-alt rounded-3xl p-6 sm:p-8 border border-rf-ink/5" data-testid="family-plan-member">
        <div className="flex items-center gap-2 mb-2">
          <Users size={20} weight="duotone" className="text-rf-orange" />
          <h2 className="font-display text-xl font-black text-rf-ink">Family Plan — Member</h2>
        </div>
        <p className="text-sm text-rf-ink-soft">
          You&apos;re on <strong>{membership.owner_name}&apos;s</strong> Family Plan. Your subscription is active until{" "}
          <strong>{new Date(membership.expires_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</strong> —
          renewal is handled by the plan owner.
        </p>
      </section>
    );
  }

  // ====== OWNER ======
  return (
    <section className="bg-white rounded-3xl p-6 sm:p-8 border border-rf-ink/10" data-testid="family-plan-owner">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Users size={22} weight="duotone" className="text-rf-orange" />
            <h2 className="font-display text-2xl font-black text-rf-ink" data-testid="family-plan-title">
              Family Plan ({(plan.max_members || 5) - plan.slots_remaining} of {plan.max_members || 5} seats used)
            </h2>
            {plan.active ? (
              <span className="chip !bg-emerald-500/20 !text-emerald-800">Active</span>
            ) : (
              <span className="chip !bg-red-500/20 !text-red-800">Expired</span>
            )}
          </div>
          <p className="text-sm text-rf-ink-soft" data-testid="family-plan-renewal">
            {plan.auto_renew ? "Renews" : "Active until"} <strong>{new Date(plan.expires_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</strong>{plan.auto_renew ? " for £100." : "."}{" "}
            You&apos;re member #1 — invite up to 4 more people by email or link, and remove or replace members any time. Members keep their own private recipes and cookbooks.
          </p>
          <a href="#family-plan-invites-list" className="text-sm font-bold text-rf-orange hover:underline" data-testid="family-plan-manage">Manage members ↓</a>
        </div>
        <div className="text-right">
          <p className="font-display text-4xl font-black text-rf-orange leading-none" data-testid="family-plan-slots-remaining">{plan.slots_remaining}</p>
          <p className="text-xs uppercase tracking-wider font-bold text-rf-ink-soft mt-1">slots left</p>
        </div>
      </div>

      {/* How it works — collapsible info box so first-time owners understand the flow */}
      <details className="rounded-xl bg-rf-cream-alt p-4 text-sm text-rf-ink-soft mb-5" data-testid="family-plan-how">
        <summary className="font-bold text-rf-ink cursor-pointer">How does the Family Plan work?</summary>
        <ul className="mt-3 space-y-1.5 list-disc ml-5">
          <li>Enter a name + email below. Each invitee gets an email with their own activation code (and the code is shown here too so you can resend it).</li>
          <li>Invitees click the activation link, sign up (or sign in), and their account is attached to your plan for the same period as your subscription.</li>
          <li>Codes don&apos;t expire — but you can revoke a pending code any time to free the slot.</li>
          <li>When your plan renews, your members&apos; access renews with it. If you cancel, members keep access until the paid period ends.</li>
          <li>Didn&apos;t fill all 4 slots at the start? Come back here any time to add more.</li>
        </ul>
      </details>

      {/* Invite form — only shown while there's at least one slot left */}
      {plan.slots_remaining > 0 && (
        <form onSubmit={invite} className="mb-5 grid gap-3 md:grid-cols-[1fr_1fr_auto] md:items-end" data-testid="family-plan-invite-form">
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-rf-ink-soft">Invitee name</label>
            <input
              type="text" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Nan Smith"
              data-testid="family-plan-invite-name"
              className="input-rf w-full !py-2"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-rf-ink-soft">Email</label>
            <input
              type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="nan@example.com"
              data-testid="family-plan-invite-email"
              className="input-rf w-full !py-2"
            />
          </div>
          <button
            type="submit" disabled={busy}
            data-testid="family-plan-invite-submit"
            className="btn-amber !py-2 !px-5 text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <UserPlus size={16} weight="bold" />
            {busy ? "Sending…" : "Send invite"}
          </button>
        </form>
      )}

      {plan.slots_remaining === 0 && (
        <p className="text-xs italic text-rf-ink-soft mb-5" data-testid="family-plan-full">
          Your plan is full — all 4 invite slots are either joined or pending. Revoke a pending one below to free a slot.
        </p>
      )}

      {/* Invites list */}
      {invites.length === 0 ? (
        <p className="text-sm text-rf-ink-soft italic" data-testid="family-plan-no-invites">No invites sent yet.</p>
      ) : (
        <ul id="family-plan-invites-list" className="divide-y divide-rf-ink/5 border border-rf-ink/10 rounded-xl overflow-hidden" data-testid="family-plan-invites-list">
          {invites.map((inv) => (
            <li key={inv.code} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`family-plan-invite-${inv.code}`}>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-rf-ink text-sm truncate">
                  {inv.status === "accepted" && inv.joined_name ? inv.joined_name : inv.invitee_name}
                </p>
                <p className="text-xs text-rf-ink-soft truncate">
                  {inv.status === "accepted" && inv.joined_email ? inv.joined_email : inv.invitee_email}
                  {inv.status === "accepted" && inv.joined_email && inv.joined_email !== inv.invitee_email && (
                    <span className="italic"> (invited as {inv.invitee_email})</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {inv.status === "pending" && (
                  <>
                    <code
                      className="text-[11px] font-mono bg-rf-cream-alt px-2 py-1 rounded cursor-pointer hover:bg-rf-amber/20"
                      title="Click to copy"
                      onClick={() => copyCode(inv.code)}
                      data-testid={`family-plan-code-${inv.code}`}
                    >
                      {inv.code}
                    </code>
                    <button
                      type="button" onClick={() => copyCode(inv.code)} disabled={busy}
                      className="p-1 text-rf-ink-soft hover:text-rf-orange"
                      title="Copy code" data-testid={`family-plan-copy-${inv.code}`}
                    ><Copy size={14} /></button>
                    <span className="chip !bg-rf-amber/30 !text-rf-ink text-[10px]">Invited</span>
                    <button
                      type="button" onClick={() => revoke(inv.code)} disabled={busy}
                      className="p-1 text-rf-ink-soft hover:text-red-600"
                      title="Revoke invite" data-testid={`family-plan-revoke-${inv.code}`}
                    ><Trash size={14} /></button>
                  </>
                )}
                {inv.status === "accepted" && (
                  <>
                    <span className="chip !bg-emerald-500/20 !text-emerald-800 inline-flex items-center gap-1">
                      <Check size={12} weight="bold" /> Joined
                    </span>
                    <button type="button" onClick={() => removeMember(inv)} disabled={busy}
                            data-testid={`family-plan-remove-${inv.code}`} className="text-xs font-bold text-red-700 hover:underline">
                      Remove
                    </button>
                  </>
                )}
                {inv.status === "removed" && (
                  <span className="chip !bg-rf-ink/10 !text-rf-ink-soft text-[10px]">Removed</span>
                )}
                {inv.status === "revoked" && (
                  <span className="chip !bg-rf-ink/10 !text-rf-ink-soft text-[10px]">Revoked</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
